import { useState, useEffect, useCallback, useMemo } from 'react';
import { notification } from 'tea-component';
import { manageStudioEyesStatus } from '@sg/services/index';

/** Local state / card prop key (BurnRateCard reads smallChartViewState.burnrate). */
export type TStudioEyesViewKey =
  | 'financials'
  | 'burnrate'
  | 'headcount'
  | 'staffrate';

/** One eye toggle: UI key + full cond API key (no prefix concatenation). */
export interface IStudioEyesViewKeyConfig {
  viewKey: TStudioEyesViewKey;
  apiKey: string;
}

/** Studios page eye toggles (stable module ref — safe for hook deps). */
export const STUDIOS_EYES_VIEW_KEYS: IStudioEyesViewKeyConfig[] = [
  { viewKey: 'financials', apiKey: 'financials' },
  { viewKey: 'burnrate', apiKey: 'burn_rate' },
  { viewKey: 'headcount', apiKey: 'headcount' },
  { viewKey: 'staffrate', apiKey: 'staff_rate' },
];

/** Portfolio Overview eye toggles — apiKey includes portfolio scope explicitly. */
export const PORTFOLIO_EYES_VIEW_KEYS: IStudioEyesViewKeyConfig[] = [
  { viewKey: 'burnrate', apiKey: 'portfolio_burn_rate' },
  { viewKey: 'headcount', apiKey: 'portfolio_headcount' },
  { viewKey: 'staffrate', apiKey: 'portfolio_staff_rate' },
];

const buildDefaultViewState = (
  viewKeys: IStudioEyesViewKeyConfig[],
): Record<string, boolean> => (
  viewKeys.reduce<Record<string, boolean>>((acc, { viewKey }) => {
    acc[viewKey] = false;
    return acc;
  }, {})
);

export interface IUseStudioEyesViewStateOptions {
  /** Which eye toggles to load/save — pass a stable module-level constant. */
  viewKeys: IStudioEyesViewKeyConfig[];
}

export const useStudioEyesViewState = ({
  viewKeys,
}: IUseStudioEyesViewStateOptions) => {
  // Stable string for effect deps (module-level arrays keep this constant).
  const viewKeysKey = viewKeys.map(({ viewKey, apiKey }) => `${viewKey}:${apiKey}`).join(',');
  const [smallChartViewState, setSmallChartViewState] = useState<Record<string, boolean>>(
    () => buildDefaultViewState(viewKeys),
  );

  const apiKeyByViewKey = useMemo(() => (
    viewKeys.reduce<Partial<Record<TStudioEyesViewKey, string>>>((acc, { viewKey, apiKey }) => {
      acc[viewKey] = apiKey;
      return acc;
    }, {})
  ), [viewKeysKey]);

  const updateStudioEyesViewState = useCallback(
    async (viewKey: TStudioEyesViewKey, enabled: boolean) => {
      const apiKey = apiKeyByViewKey[viewKey];
      if (!apiKey) {
        return;
      }

      setSmallChartViewState((prev) => ({
        ...prev,
        [viewKey]: enabled,
      }));

      try {
        await manageStudioEyesStatus({
          op: 'set',
          key: apiKey,
          filter: enabled ? '1' : '0',
        });
      } catch (error) {
        notification.error({
          description: 'Failed to save card view setting',
        });
      }
    },
    [apiKeyByViewKey],
  );

  useEffect(() => {
    const configs = viewKeysKey.split(',').map((entry) => {
      const [viewKey, apiKey] = entry.split(':');
      return { viewKey: viewKey as TStudioEyesViewKey, apiKey };
    });

    const fetchEyesStatus = async () => {
      try {
        const res = await manageStudioEyesStatus({
          op: 'get',
          key: configs.map(({ apiKey }) => apiKey),
        });
        const data = res?.data || {};
        const nextState = buildDefaultViewState(configs);
        configs.forEach(({ viewKey, apiKey }) => {
          nextState[viewKey] = String(data?.[apiKey]) === '1';
        });
        setSmallChartViewState(nextState);
      } catch (error) {
        setSmallChartViewState(buildDefaultViewState(configs));
      }
    };
    fetchEyesStatus();
  }, [viewKeysKey]);

  return {
    smallChartViewState,
    updateStudioEyesViewState,
  };
};
